'use strict';

import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {useSelector} from 'react-redux';
import _ from 'lodash';
import parse from 'html-react-parser';
import {keys} from 'ts-transformer-keys';

import {Button} from 'primereact/button';
import {
    Dropdown,
    DropdownProps,
    DropdownChangeEvent
} from 'primereact/dropdown';
import {
    RadioButton,
    RadioButtonProps,
    RadioButtonChangeEvent
} from 'primereact/radiobutton';
import {Tooltip} from 'primereact/tooltip';

import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import {PropertySetValue} from '~/shared/src/mm/PropertySet';
import {DataDescriptorValues} from '~/shared/src/utilities/condition';
import {Style} from '~/shared/src/utilities/style';
import {defaultBaseRequestParameters} from '~/shared/src/utilities/modules/common';
import {
    ValueSetListingOrder,
    ValueSetFieldPresentation
} from '~/shared/src/utilities/modules/form';
import messageUtils from '~/shared/src/utilities/messageUtils';

import FieldWrapper, {
    DefaultProps as FieldWrapperDefaultProps,
    defaultProps as fieldWrapperDefaultProps,
    Props as FieldWrapperProps,
    ComponentProps as FieldWrapperComponentProps,
    componentProps as fieldWrapperComponentProps
} from '~/frontend/src/components/base/shared/field/FieldWrapper';

import {getValueDisplayName} from '~/frontend/src/utilities/renderHelpers';
import {StoreState} from '~/frontend/src/store';
import {
    isInvalidHTML,
    resolveStyleToClassNames
} from '~/frontend/src/utilities/valueRenderers';


type InputProps = Omit<DropdownProps, 'autoComplete' | 'onChange' | 'key'>;
const inputProps = keys<InputProps>();

type InputChangeProps = DropdownChangeEvent;

type DefaultProps = FieldWrapperDefaultProps & {
    schema? : typeof defaultBaseRequestParameters[ 'schema' ];

    /**
     * How to present the value selection.
     */
    presentation? : ValueSetFieldPresentation;

    /**
     * The order directive for the value selection
     */
    listingOrder? : ValueSetListingOrder;

    /**
     * The number of columns to render the radio options with.
     * If set to 0 the number will be automatically computed to show all options in a row.
     *
     * Only applicable if <prop>presentation</prop> is set to 'radio'.
     */
    radioColumns? : number;

    /**
     * Show only listed options
     */
    whitelist? : string[] | {
        [ value : string ] : string
    };

    /**
     * Hide listed options
     */
    blacklist? : string[] | {
        [ value : string ] : string
    };

    /**
     * Allows the selection of none value.
     */
    allowNone? : boolean;

    /**
     * Should the value shown as defined (raw) <strong>or</strong> should they be formatted.
     */
    rawMode? : boolean;

    /**
     * Should the MetaModel information as defined additionally shown for processed information <strong>or</strong> not.
     * This applies in the form: &lt;processed MetaModel information&gt; (&lt;defined MetaModel information&gt;)
     *
     * Only applicable if <prop>rawMode</prop> is set to false.
     */
    showAdditionalRawNames? : boolean;

    /**
     * Should the field marked if dirty.
     */
    markDirty? : boolean;

    /**
     * The CSS class names to style the input field with.
     */
    inputClassNames? : string[];

    /**
     * The styles to style the input field with.
     */
    inputStyle? : React.CSSProperties;

    /**
     * Values for resolving the style.
     * dataDescriptor.alias to value mapping
     */
    valuesForValueStyle? : DataDescriptorValues;

    /**
     * The dataDescriptor.alias for this field.
     */
    dataDescriptorAlias? : string;
};
const defaultProps : Required<DefaultProps> = {
    schema: defaultBaseRequestParameters[ 'schema' ],

    ...fieldWrapperDefaultProps,

    presentation          : 'dropdown',
    listingOrder          : 'order',
    radioColumns          : 1,
    whitelist             : [],
    blacklist             : [],
    allowNone             : true,
    rawMode               : false,
    showAdditionalRawNames: false,
    markDirty             : false,
    inputClassNames       : [],
    inputStyle            : {},
    valuesForValueStyle   : {},
    dataDescriptorAlias   : ''
};

type CommonProps = Omit<FieldWrapperProps, 'pt'> & {

    /**
     * The value.
     */
    value? : AttributeValue;

    /**
     * The value to determine dirty against.
     */
    originalValue? : AttributeValue;

    /**
     * Conditional style for the value(s).
     */
    valueStyle? : Style[];

    onChange : ( value : AttributeValue | undefined, originalEvent : any ) => void;

    pt? : {
        fieldWrapper? : FieldWrapperProps[ 'pt' ];
    };
};
type Props = CommonProps & (
    {

        /**
         * The PropertySet ID.
         */
        propertySetId : number;

        referencePropertySetId? : never;
        valueSetId? : never;
    } |
    {

        /**
         * The ReferencePropertySet ID.
         */
        referencePropertySetId : number;

        propertySetId? : never;
        valueSetId? : never;
    } |
    {

        /**
         * The ValueSet ID.
         */
        valueSetId : number;

        propertySetId? : never;
        referencePropertySetId? : never;
    }
    );

export type ComponentProps = InputProps & Props & DefaultProps;
export const componentProps = keys<ComponentProps>();

type AllProps = InputProps & Props & Required<DefaultProps>;

type ValueObject = {
    id : number;
    label : string;
    parsedLabel : React.ReactNode;
    value? : AttributeValue;
    stringValue? : string;
    sortOrder? : number;
};

// outside the default values are not required (AllProps),
// but inside they are required (set to default) for static type checking (AllPropsWithDefaultsSet)
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const ValueSetField = React.forwardRef<Dropdown, ComponentProps>( ( props : AllProps, forwardedRef ) => {
    // avoid rendering if hidden
    if ( props.visibility === 'hidden' ) {
        return null;
    }
    else {
        useSelector( ( state : StoreState ) => state.session?.user );

        const {
                  uid,
                  allowNone,
                  appendToElementId,
                  blacklist,
                  dataDescriptorAlias,
                  disabled,
                  displayValue,
                  displayClassNames,
                  errorMessage,
                  id,
                  inputClassNames,
                  inputStyle,
                  listingOrder,
                  markDirty,
                  messagePlaceholders,
                  messageTemplateStringResolver,
                  name,
                  onChange,
                  originalValue,
                  placeholder,
                  presentation,
                  propertySetId,
                  pt,
                  radioColumns,
                  rawMode,
                  referencePropertySetId,
                  schema,
                  showAdditionalRawNames,
                  tabIndex,
                  tooltip,
                  tooltipDisabled,
                  tooltipOptions,
                  value,
                  valueSetId,
                  valueStyle,
                  valuesForValueStyle,
                  whitelist
              }                                               = props,
              elementId                                       = id ?? `${uid}_valueSetField`,
              inputRef                                        = useRef<Dropdown | null>( null ),
              [ elementValue, setElementValue ]               = useState<AttributeValue | undefined>( value ),
              [ appendTo, setAppendTo ]                       = useState<'self' | HTMLElement>( 'self' ),
              [ overlay, setOverlay ]                         = useState<boolean>( false ),
              messageResolver                                 = useMemo(
                  () => {
                      if ( !messageTemplateStringResolver ) {
                          return ( messageKey : string ) : string | undefined => messageKey === '' ? undefined : __( messageKey, messagePlaceholders, false );
                      }
                      else {
                          return ( messageKey : string ) => messageKey === '' ? undefined : __( messageTemplateStringResolver( __( messageKey, messagePlaceholders ) ) ?? '', undefined, false );
                      }
                  },
                  [
                      JSON.stringify( messagePlaceholders ),
                      messageTemplateStringResolver
                  ]
              ),
              valueStyleInputClassNames                       = useMemo(
                  () => {
                      let retVal : string[] = [];

                      if ( valueStyle ) {
                          retVal = resolveStyleToClassNames(
                              valueStyle,
                              {
                                  ...valuesForValueStyle,
                                  [ dataDescriptorAlias ]: elementValue
                              }
                          );
                      }

                      return retVal;
                  },
                  [
                      dataDescriptorAlias,
                      elementValue,
                      valuesForValueStyle,
                      valueStyle
                  ]
              ),
              inputClassNamesWithoutValueStyleInputClassNames = useMemo(
                  () => {
                      return inputClassNames.reduce(
                          ( reducedClassNames, className ) => {
                              if ( !valueStyleInputClassNames.includes( className ) ) {
                                  reducedClassNames.push( className );
                              }

                              return reducedClassNames;
                          },
                          [] as string[]
                      )
                  },
                  [
                      inputClassNames,
                      valueStyleInputClassNames
                  ]
              ),
              optionsWhitelist                                = useMemo(
                  () => {
                      return Array.isArray( whitelist ) ? whitelist : Object.keys( whitelist ) as string[];
                  },
                  [
                      whitelist
                  ]
              ),
              optionsBlacklist                                = useMemo(
                  () => {
                      return Array.isArray( blacklist ) ? blacklist : Object.keys( blacklist ) as string[];
                  },
                  [
                      blacklist
                  ]
              ),
              displayValueLabel                               = useMemo(
                  () => {
                      let retVal : React.ReactNode | undefined,
                          valueSetIdToUse = valueSetId;

                      if ( propertySetId !== undefined ) {
                          const propertySet = metaModel[ schema ].getPropertySet( propertySetId );

                          valueSetIdToUse = propertySet?.valueSetId;
                      }
                      else if ( referencePropertySetId !== undefined ) {
                          const propertySet = metaModel[ schema ].getReferencePropertySet( referencePropertySetId );

                          valueSetIdToUse = propertySet?.valueSetId;
                      }

                      if ( displayValue ) {
                          if ( valueSetIdToUse && typeof displayValue === 'string' ) {
                              retVal = parse( getValueDisplayName( valueSetIdToUse, displayValue, schema, rawMode, showAdditionalRawNames ) );
                          }
                          else {
                              retVal = displayValue;
                          }
                      }

                      return retVal;
                  },
                  [
                      displayValue,
                      propertySetId,
                      rawMode,
                      referencePropertySetId,
                      schema,
                      showAdditionalRawNames,
                      user ? user.language : undefined,
                      valueSetId
                  ]
              ),
              options                                         = useMemo(
                  () => {
                      let valueSetValues : PropertySetValue[] | undefined;

                      if ( propertySetId !== undefined ) {
                          valueSetValues = metaModel[ schema ].getPropertySet( propertySetId )?.getValueSetValues;
                      }
                      else if ( referencePropertySetId !== undefined ) {
                          valueSetValues = metaModel[ schema ].getReferencePropertySet( referencePropertySetId )?.getValueSetValues;
                      }
                      else if ( valueSetId !== undefined ) {
                          valueSetValues = metaModel[ schema ].getValueSetValues( valueSetId ).map( vSV => {
                              return {
                                  id         : vSV.id,
                                  valueSetId : vSV.valueSetId,
                                  statusId   : vSV.statusId,
                                  stringValue: vSV.value,
                                  value      : vSV.value,
                                  sortOrder  : vSV.sortOrder
                              } as PropertySetValue;
                          } );
                      }

                      const data : ValueObject[] = valueSetValues?.reduce( ( innerRetVal : ValueObject[], valueObj : PropertySetValue ) : ValueObject[] => {
                          if (
                              ( !optionsWhitelist.length || optionsWhitelist.includes( valueObj.stringValue ) )
                              && !optionsBlacklist.includes( valueObj.stringValue )
                          ) {
                              const label = getValueDisplayName( valueObj.valueSetId, valueObj.id, schema, rawMode, showAdditionalRawNames );

                              innerRetVal.push( {
                                  id         : valueObj.id,
                                  value      : valueObj.value,
                                  stringValue: valueObj.stringValue,
                                  label,
                                  parsedLabel: parse( label ),
                                  sortOrder  : valueObj.sortOrder
                              } );
                          }

                          return innerRetVal;
                      }, [] ) ?? [];

                      // sort
                      data.sort( ( val1 : ValueObject, val2 : ValueObject ) => {
                          if ( listingOrder === 'order' ) {
                              if ( ( val1.sortOrder ?? 0 ) > ( val2.sortOrder ?? 0 ) ) {
                                  return 1;
                              }
                              else if ( ( val1.sortOrder ?? 0 ) < ( val2.sortOrder ?? 0 ) ) {
                                  return -1;
                              }
                              else {
                                  return 0;
                              }
                          }
                          else {
                              if ( ( val1.label ) > ( val2.label ) ) {
                                  return listingOrder === 'asc' ? 1 : -1;
                              }
                              else if ( ( val1.label ) < ( val2.label ) ) {
                                  return listingOrder === 'asc' ? -1 : 1;
                              }
                              else {
                                  return 0;
                              }
                          }
                      } );

                      return data;
                  },
                  [
                      allowNone,
                      blacklist,
                      listingOrder,
                      propertySetId,
                      rawMode,
                      referencePropertySetId,
                      schema,
                      showAdditionalRawNames,
                      user ? user.language : undefined,
                      valueSetId,
                      whitelist
                  ]
              ),
              itemTemplate                                    = useCallback(
                  ( option : any ) => {
                      let retVal : JSX.Element;

                      if ( option.id === 0 ) {
                          retVal = <span className="p-text-secondary">{option.parsedLabel}</span>
                      }
                      else {
                          let styleInputClassNames : string[] = [];

                          // style
                          if ( valueStyle ) {
                              styleInputClassNames = resolveStyleToClassNames(
                                  valueStyle,
                                  {
                                      ...valuesForValueStyle,
                                      [ dataDescriptorAlias ]: option.value
                                  }
                              );
                          }

                          retVal = <div
                              className={`yn-valuesetfield-option ${inputClassNamesWithoutValueStyleInputClassNames.concat( styleInputClassNames ).join( ' ' )}`}
                              style={{ ...defaultProps.inputStyle, ...inputStyle }}
                          >
                              {option.parsedLabel}
                          </div>;
                      }

                      return retVal;
                  },
                  [
                      dataDescriptorAlias,
                      inputClassNamesWithoutValueStyleInputClassNames,
                      inputStyle,
                      valueStyle,
                      valuesForValueStyle
                  ]
              ),
              valueTemplate                                   = useCallback(
                  ( option : any, dropdownProps : DropdownProps ) => {
                      let retVal : string | JSX.Element = <>{dropdownProps.placeholder ?? '&nbsp;'}</>;

                      if ( option ) {
                          retVal = <div
                              className={`yn-valuesetfield-value ${inputClassNamesWithoutValueStyleInputClassNames.concat( valueStyleInputClassNames ).join( ' ' )}`}
                              style={{ ...defaultProps.inputStyle, ...inputStyle }}
                          >
                              {option.parsedLabel}
                          </div>;
                      }

                      return retVal;
                  },
                  [
                      inputClassNamesWithoutValueStyleInputClassNames,
                      inputStyle,
                      valueStyleInputClassNames
                  ]
              ),
              onInputShow                                     = useCallback(
                  () => {
                      let overlayElement : HTMLElement | undefined,
                          targetElement : HTMLElement | undefined;

                      setOverlay( true );

                      if ( inputRef.current?.getOverlay() ) {
                          overlayElement = inputRef.current.getOverlay();
                          targetElement = inputRef.current.getElement();
                      }

                      if ( overlayElement && targetElement ) {
                          if ( overlayElement.style.transformOrigin.indexOf( 'bottom' ) !== -1 ) {
                              overlayElement.style.marginTop = '-1px';
                          }
                          else {
                              overlayElement.style.marginTop = '1px';
                          }
                      }
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onInputHide                                     = useCallback(
                  () => {
                      setOverlay( false );
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onRadioKeyDown                                  = useCallback(
                  ( changedValue : AttributeValue | undefined, event : React.KeyboardEvent<HTMLDivElement> ) => {
                      if ( changedValue !== value ) {
                          switch ( event.key ) {
                              // enter
                              case 'Enter':
                              // space
                              case ' ':
                                  setElementValue( changedValue );

                                  onChange( changedValue, event );

                                  event.stopPropagation();
                                  event.preventDefault();
                          }
                      }
                  },
                  [
                      onChange
                  ]
              );

        let valueSetElement : JSX.Element,
            onInputChange : DropdownProps[ 'onChange' ] | RadioButtonProps[ 'onChange' ],
            onInputClick : ( inputValue : AttributeValue | undefined, event : React.MouseEvent ) => void;

        if ( presentation === 'dropdown' ) {
            onInputChange = ( event : InputChangeProps ) => {
                let changedValue = event.target.value;

                if ( changedValue !== elementValue ) {
                    if ( changedValue === null ) {
                        changedValue = undefined;
                    }

                    setElementValue( changedValue );

                    onChange( changedValue, event );
                }
            };

            valueSetElement = <Dropdown
                {..._.pick( props, inputProps ) as InputProps}

                id={elementId}

                ref={( node ) => {
                    inputRef.current = node;
                    if ( typeof forwardedRef === 'function' ) {
                        forwardedRef( node );
                    }
                    else if ( forwardedRef ) {
                        forwardedRef.current = node;
                    }
                }}

                options={options}
                optionValue="value"
                optionLabel="label"

                value={elementValue}
                onChange={onInputChange}
                onShow={onInputShow}
                onHide={onInputHide}

                valueTemplate={valueTemplate}
                itemTemplate={itemTemplate}

                showClear={allowNone}

                className={`yn-valuesetfield yn-valuesetfield-dropdown ${inputClassNamesWithoutValueStyleInputClassNames.concat( valueStyleInputClassNames ).join( ' ' )}`}
                style={{ ...defaultProps.inputStyle, ...inputStyle }}

                placeholder={messageResolver( placeholder ?? '' )}
                tooltip={undefined}
                tooltipOptions={undefined}

                appendTo={appendTo}
                panelStyle={appendTo !== 'self' ? { margin: '1px 0' } : undefined}

                aria-describedby={`${elementId}_message`}
            />;
        }
        else if ( presentation === 'radio' ) {
            onInputChange = ( event : RadioButtonChangeEvent ) => {
                const changedValue = event.value;

                if ( changedValue !== value ) {
                    setElementValue( changedValue );

                    onChange( changedValue, event );
                }
                else if ( allowNone && !event.checked ) {
                    setElementValue( undefined );

                    onChange( undefined, event );
                }
            };
            onInputClick = ( optionValue : AttributeValue | undefined, event : React.MouseEvent ) => {
                const changedValue = optionValue;

                if ( allowNone && changedValue === value ) {
                    setElementValue( undefined );

                    onChange( undefined, event );
                }
            }

            valueSetElement = <div className="yn-valuesetfield yn-valuesetfield-radio" style={{ columnCount: radioColumns || 1 }}>
                {options.map( ( option, optionIndex ) => {
                    const optionElementId             = `${elementId}_${option.id}`,
                          optionClassNames : string[] = [ 'yn-valuesetfield-option' ];

                    let checked                         = option.value === elementValue,
                        styleInputClassNames : string[] = [];

                    // style
                    if ( valueStyle ) {
                        styleInputClassNames = resolveStyleToClassNames(
                            valueStyle,
                            {
                                ...valuesForValueStyle,
                                [ dataDescriptorAlias ]: option.value
                            }
                        );
                    }

                    if ( elementValue === undefined && option.stringValue === displayValue ) {
                        checked = true;
                        optionClassNames.push( `yn-valuesetfield-option-${displayClassNames.join( ' ' ).indexOf( 'property-inherited' ) !== -1 ? 'inherited' : 'default'}` );
                    }
                    if ( errorMessage ) {
                        optionClassNames.push( 'yn-error-border' );
                    }
                    else if (
                        markDirty
                        && (
                            ( option.value !== elementValue && option.value === originalValue )
                            || ( option.value === elementValue && option.value !== originalValue )
                        )
                    ) {
                        optionClassNames.push( 'yn-dirty-border' );
                    }

                    return <div key={optionElementId} className="field-radiobutton">
                        <RadioButton
                            inputId={optionElementId}
                            name={name}

                            value={option.value}
                            onChange={onInputChange}
                            pt={{
                                root: {
                                    onClick: onInputClick.bind( null, option.value )
                                }
                            }}

                            checked={checked}
                            disabled={disabled}

                            className={optionClassNames.join( ' ' )}

                            tooltip={undefined}
                            tooltipOptions={undefined}

                            tabIndex={( tabIndex ?? 0 ) + optionIndex}
                            onKeyDown={onRadioKeyDown.bind( null, option.value )}
                        />
                        <label
                            htmlFor={optionElementId}
                            className={`p-radiobutton-label ${inputClassNamesWithoutValueStyleInputClassNames.concat( styleInputClassNames ).join( ' ' )}`}
                            style={{ ...defaultProps.inputStyle, ...inputStyle }}
                        >
                            {option.parsedLabel}
                        </label>
                    </div>;
                } )}
            </div>;
        }
        else /*if ( presentation === 'button' )*/ {
            onInputClick = ( buttonValue : AttributeValue | undefined, event : React.MouseEvent ) => {
                const changedValue = buttonValue;

                if ( changedValue !== value ) {
                    setElementValue( changedValue );

                    onChange( changedValue, event );
                }
                else if ( allowNone ) {
                    setElementValue( undefined );

                    onChange( undefined, event );
                }
            };

            const elementClassNames : string[] = [ 'yn-valuesetfield', 'yn-valuesetfield-buttonset', 'p-buttonset' ];

            if ( errorMessage ) {
                elementClassNames.push( 'yn-error-border' );
            }
            else if ( markDirty && elementValue !== originalValue ) {
                elementClassNames.push( 'yn-dirty-border' );
            }

            valueSetElement = <span
                id={elementId}
                className={elementClassNames.join( ' ' )}
            >
                {options.map( ( option, optionIndex ) => {
                    const buttonElementId             = `${elementId}_${option.id}`,
                          buttonClassNames : string[] = [ 'yn-valuesetfield-button' ];

                    let buttonTooltip : React.ReactNode | string | undefined,
                        styleInputClassNames : string[] = [];

                    // tooltip
                    if ( tooltip ) {
                        if ( typeof tooltip === 'string' ) {
                            const regExp = new RegExp( `\\$\\{(?!${messagePlaceholders ? Object.keys( messagePlaceholders ).join( '|') + '|' : ''}option)`, 'g' );

                            buttonTooltip = __( tooltip ).replaceAll( regExp, `\\$\\{` );
                            buttonTooltip = messageUtils.buildReturnFunction( { ...messagePlaceholders, option } )( ( buttonTooltip as string ) );

                            if ( messageTemplateStringResolver && typeof buttonTooltip === 'string' ) {
                                buttonTooltip = messageTemplateStringResolver( buttonTooltip );
                            }

                            if ( typeof buttonTooltip === 'string' && !isInvalidHTML( buttonTooltip ) ) {
                                buttonTooltip = parse( buttonTooltip )
                            }
                        }
                        else {
                            buttonTooltip = tooltip;
                        }
                    }

                    // style
                    if ( valueStyle ) {
                        styleInputClassNames = resolveStyleToClassNames(
                            valueStyle,
                            {
                                ...valuesForValueStyle,
                                [ dataDescriptorAlias ]: option.value
                            }
                        );
                    }

                    if ( elementValue !== option.value ) {
                        buttonClassNames.push( 'p-button-outlined' );
                    }

                    if ( elementValue === undefined && option.stringValue === displayValue ) {
                        buttonClassNames.push( `yn-valuesetfield-button-${displayClassNames.join( ' ' ).indexOf( 'property-inherited' ) !== -1 ? 'inherited' : 'default'}` );
                    }

                    let retVal = <Button
                        key={buttonElementId}
                        id={buttonElementId}
                        name={name}

                        onClick={onInputClick.bind( null, option.value )}

                        disabled={disabled}
                        label={option.parsedLabel as string}
                        className={buttonClassNames.concat( inputClassNamesWithoutValueStyleInputClassNames ).concat( styleInputClassNames ).join( ' ' )}
                        style={{ ...defaultProps.inputStyle, ...inputStyle }}

                        tooltip={undefined}
                        tooltipOptions={undefined}

                        tabIndex={( tabIndex ?? 0 ) + optionIndex}
                    />;

                    if ( tooltip ) {
                        retVal = <>
                            <Tooltip
                                position="top"
                                {...tooltipOptions}
                                target={`#${buttonElementId}`}
                            >
                                {buttonTooltip}
                            </Tooltip>
                            {retVal}
                        </>;
                    }

                    return retVal;
                } )}
            </span>;
        }

        useEffect(
            () => {
                setElementValue( value );
            },
            [
                value
            ]
        );

        // resolve appendTo
        useLayoutEffect(
            () => {
                if ( appendToElementId && appendToElementId !== 'self' ) {
                    let appendToElement : HTMLElement | null;

                    if ( appendToElementId !== 'document' ) {
                        appendToElement = document.getElementById( appendToElementId );
                    }
                    else {
                        appendToElement = document.body;
                    }

                    if ( appendToElement && appendToElement !== appendTo ) {
                        setAppendTo( appendToElement );
                    }
                }
            }
        );

        return <FieldWrapper
            {..._.pick( props, fieldWrapperComponentProps ) as FieldWrapperComponentProps}

            uid={elementId}

            isDirty={presentation !== 'radio' && markDirty && value !== originalValue}
            displayValue={presentation === 'dropdown' ? displayValueLabel : undefined}
            displayValueWidthOffsetRem={presentation === 'dropdown' ? 2.357 : undefined}
            displayValueWidthOffsetPx={presentation === 'dropdown' ? 1 : undefined}
            tooltipDisabled={tooltipDisabled || overlay || presentation === 'button'}

            autoComplete={false}
            pt={pt?.fieldWrapper}
        >
            {valueSetElement}
        </FieldWrapper>;
    }
} );
ValueSetField.defaultProps = defaultProps;
ValueSetField.displayName = 'ValueSetField';

export default ValueSetField;
